import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, LogIn, Mail, Plus, UserCog } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { consoleFor } from '@/lib/roles'
import { useAuth } from '@/context/AuthContext'
import { useAdminDepartments } from '@/hooks/useAdminDepartments'
import type { Paginated, User } from '@/types'
import AvatarUploader from '@/components/AvatarUploader'
import Avatar from '@/components/ui/Avatar'
import Badge from '@/components/ui/Badge'
import Button from '@/components/ui/Button'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import Modal from '@/components/ui/Modal'
import PageHeader from '@/components/ui/PageHeader'
import Pager from '@/components/ui/Pager'
import { Field, Input } from '@/components/ui/Field'
import SetPasswordModal from '@/components/admin/SetPasswordModal'
import { Select } from '@/components/ui/Select'
import { Table, Row, Cell, EmptyRow, actionsColumn, actionsCellClass } from '@/components/ui/Table'
import { cn } from '@/lib/cn'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'

interface TeamMember {
  id: number
  name: string
  email: string
  avatar_url: string | null
  has_custom_avatar: boolean
  role: 'admin' | 'staff' | 'sales'
  department: { id: number; name: string } | null
  status: 'approved' | 'suspended'
  joined_at: string | null
}

interface Draft {
  id?: number
  name: string
  email: string
  role: 'admin' | 'staff' | 'sales'
  department_id: string
  status: 'approved' | 'suspended'
}

const emptyDraft: Draft = { name: '', email: '', role: 'staff', department_id: '', status: 'approved' }

function toDraft(member: TeamMember): Draft {
  return {
    id: member.id,
    name: member.name,
    email: member.email,
    role: member.role,
    department_id: member.department ? String(member.department.id) : '',
    status: member.status,
  }
}

/** The console a member lands in, named the way the sidebar names it. */
function consoleLabel(member: TeamMember): string {
  return consoleFor({ role: member.role } as User).label
}

export default function AdminStaff() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { user: me, impersonate, refresh } = useAuth()
  const [draft, setDraft] = useState<Draft | null>(null)
  const [confirmRemove, setConfirmRemove] = useState<TeamMember | null>(null)
  const [confirmInvite, setConfirmInvite] = useState<TeamMember | null>(null)
  const [confirmSignIn, setConfirmSignIn] = useState<TeamMember | null>(null)
  const [passwordFor, setPasswordFor] = useState<TeamMember | null>(null)

  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  const team = useQuery({
    queryKey: ['admin-users', 'page', search, page],
    queryFn: async () =>
      (await api.get<Paginated<TeamMember>>('/admin/users', { params: { search: search || undefined, page } })).data,
    // Keeps the table (and the search box's focus) on screen while the next
    // page or search result loads, instead of swapping in the loader.
    placeholderData: keepPreviousData,
  })
  const members = team.data?.data

  // Sections come from the database. Hardcoding the five would break the
  // moment the client adds or renames one, the same way tier names must not
  // be hardcoded.
  const departments = useAdminDepartments()

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin-users'] })

  const save = useMutation({
    mutationFn: async (d: Draft) => {
      const payload = {
        name: d.name,
        email: d.email,
        role: d.role,
        department_id: d.role === 'staff' ? Number(d.department_id) : null,
        status: d.status,
      }
      return d.id
        ? (await api.patch(`/admin/users/${d.id}`, payload)).data
        : (await api.post('/admin/users', payload)).data
    },
    onSuccess: (res: { message?: string }) => {
      invalidate()
      setDraft(null)
      toast.success(res.message ?? 'Saved')
    },
    onError: (err) => toast.error('Save failed', errorMessage(err)),
  })

  const invite = useMutation({
    mutationFn: async (id: number) => (await api.post(`/admin/users/${id}/invite`)).data,
    onSuccess: (res: { message?: string }) => {
      setConfirmInvite(null)
      toast.success('Invite sent', res.message)
    },
    onError: (err) => {
      setConfirmInvite(null)
      toast.error('Could not send the invite', errorMessage(err))
    },
  })

  const signIn = useMutation({
    // The context swaps the user and moves to their console together.
    mutationFn: async (member: TeamMember) => impersonate({ id: member.id, kind: 'team' }),
    onSuccess: () => setConfirmSignIn(null),
    onError: (err) => {
      setConfirmSignIn(null)
      toast.error('Could not sign in as them', errorMessage(err))
    },
  })

  const remove = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/admin/users/${id}`)).data,
    onSuccess: (res: { message?: string }) => {
      invalidate()
      setConfirmRemove(null)
      toast.success(res.message ?? 'Deactivated')
    },
    onError: (err) => {
      setConfirmRemove(null)
      toast.error('Could not deactivate', errorMessage(err))
    },
  })

  const openPassword = (member: TeamMember) => setPasswordFor(member)

  if (team.isLoading) return <LogoLoader />

  const staffMissingSection = draft?.role === 'staff' && !draft.department_id
  // The member behind the open edit modal, for the Set password shortcut.
  const editing = draft?.id ? members?.find((member) => member.id === draft.id) : undefined

  return (
    <div>
      <PageHeader
        title="Staff & admins"
        subtitle="Team accounts. Customers register themselves and are managed under Customers."
        actions={
          <div className="flex w-full flex-wrap gap-2 sm:w-auto">
            <Input
              placeholder="Search name or email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="w-full sm:w-64"
            />
            <Button onClick={() => setDraft(emptyDraft)}>
              <Plus className="size-4" /> Add member
            </Button>
          </div>
        }
      />

      <Table columns={['Name', 'Email', 'Role', 'Section', 'Status', actionsColumn]}>
        {(members?.length ?? 0) === 0 && (
          <EmptyRow colSpan={6}>{search ? `Nobody matches "${search}".` : undefined}</EmptyRow>
        )}
        {members?.map((member) => {
          const isMe = member.id === me?.id
          // Admins sign in with their own account; a suspended member has
          // no console to show. The server refuses both as well.
          const canSignInAs = member.role !== 'admin' && member.status === 'approved'
          return (
            <Row
              key={member.id}
              onClick={() =>
                // Row tap opens the same edit modal as the UserCog button.
                setDraft(toDraft(member))
              }
            >
              <Cell className="font-semibold">
                <div className="flex items-center gap-2.5">
                  <Avatar user={member} className="size-8 shrink-0 bg-primary text-xs font-semibold text-white" />
                  {member.name}
                </div>
              </Cell>
              <Cell>{member.email}</Cell>
              <Cell>
                <Badge tone={member.role === 'admin' ? 'brand' : member.role === 'sales' ? 'warning' : 'neutral'}>
                  {member.role}
                </Badge>
              </Cell>
              <Cell>{member.department?.name ?? '-'}</Cell>
              <Cell>
                <Badge tone={member.status === 'approved' ? 'success' : 'danger'}>
                  {member.status === 'approved' ? 'active' : 'suspended'}
                </Badge>
              </Cell>
              <Cell className={cn(actionsCellClass, 'w-px whitespace-nowrap')}>
                <div className="flex justify-end gap-1.5">
                  <Button
                    variant="ghost"
                    className="px-2"
                    onClick={(e) => {
                      e.stopPropagation()
                      setConfirmInvite(member)
                    }}
                    title="Email a link to set their password"
                    aria-label={`Email ${member.name} a link to set their password`}
                  >
                    <Mail className="size-4" />
                  </Button>
                  {!isMe && (
                    <Button
                      variant="ghost"
                      className="px-2"
                      onClick={(e) => {
                        e.stopPropagation()
                        openPassword(member)
                      }}
                      title="Set their password"
                      aria-label={`Set a password for ${member.name}`}
                    >
                      <KeyRound className="size-4" />
                    </Button>
                  )}
                  {canSignInAs && (
                    <Button
                      variant="ghost"
                      className="px-2"
                      onClick={(e) => {
                        e.stopPropagation()
                        setConfirmSignIn(member)
                      }}
                      title="Sign in as this member"
                      aria-label={`Sign in as ${member.name}`}
                    >
                      <LogIn className="size-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    className="px-2"
                    onClick={(e) => {
                      e.stopPropagation()
                      setDraft(toDraft(member))
                    }}
                    title="Edit account"
                    aria-label={`Edit ${member.name}`}
                  >
                    <UserCog className="size-4" />
                  </Button>
                  {member.status === 'approved' && (
                    <Button
                      variant="ghost"
                      className="px-2 text-danger"
                      onClick={(e) => {
                        e.stopPropagation()
                        setConfirmRemove(member)
                      }}
                    >
                      Deactivate
                    </Button>
                  )}
                </div>
              </Cell>
            </Row>
          )
        })}
      </Table>
      <Pager meta={team.data?.meta} page={page} onPage={setPage} />

      <Modal
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft?.id ? 'Edit team member' : 'Add team member'}
        footer={
          <div className="flex w-full flex-wrap items-center justify-end gap-2">
            {editing && editing.id !== me?.id && (
              <Button
                variant="outline"
                className="mr-auto"
                onClick={() => {
                  setDraft(null)
                  openPassword(editing)
                }}
              >
                <KeyRound className="size-4" /> Set password
              </Button>
            )}
            <Button variant="outline" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              loading={save.isPending}
              disabled={!draft?.name || !draft?.email || staffMissingSection}
              onClick={() => draft && save.mutate(draft)}
            >
              Save
            </Button>
          </div>
        }
      >
        {draft && (
          <div className="space-y-4">
            {/* The photo saves on its own, like on the Profile page; Save is
                for the fields below. A new member gets one after they exist. */}
            {editing && (
              <div className="border-b border-foreground/10 pb-4">
                <AvatarUploader
                  user={editing}
                  endpoint={`/admin/users/${editing.id}/avatar`}
                  // Your own photo also sits in the top bar.
                  onChanged={() => Promise.all([invalidate(), editing.id === me?.id && refresh()])}
                />
              </div>
            )}
            <Field label="Full name" htmlFor="name">
              <Input id="name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            </Field>
            <Field label="Email" htmlFor="email">
              <Input
                id="email"
                type="email"
                value={draft.email}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              />
            </Field>
            <Field label="Role" htmlFor="role">
              <Select
                id="role"
                value={draft.role}
                onChange={(v) => setDraft({ ...draft, role: v as Draft['role'] })}
                options={[
                  { value: 'staff', label: 'Staff' },
                  { value: 'sales', label: 'Sales (PIC)' },
                  { value: 'admin', label: 'Admin' },
                ]}
              />
            </Field>
            {draft.role === 'staff' && (
              <Field label="Section" htmlFor="department_id">
                <Select
                  id="department_id"
                  value={draft.department_id}
                  onChange={(v) => setDraft({ ...draft, department_id: v })}
                  placeholder="Select a section"
                  options={(departments.data?.departments ?? []).map((dept) => ({
                    value: String(dept.id),
                    label: `${dept.sequence}. ${dept.name}`,
                  }))}
                />
              </Field>
            )}
            {draft.id && (
              <Field label="Status" htmlFor="status">
                <Select
                  id="status"
                  value={draft.status}
                  onChange={(v) => setDraft({ ...draft, status: v as Draft['status'] })}
                  options={[
                    { value: 'approved', label: 'Active' },
                    { value: 'suspended', label: 'Suspended' },
                  ]}
                />
              </Field>
            )}
            {!draft.id && (
              <p className="text-xs text-foreground/60">
                They receive an email with a link to choose their own password. You can also set one for them
                afterwards with the key button on their row.
              </p>
            )}
          </div>
        )}
      </Modal>

      <SetPasswordModal
        account={
          passwordFor
            ? { name: passwordFor.name, email: passwordFor.email, endpoint: `/admin/users/${passwordFor.id}/password` }
            : null
        }
        onClose={() => setPasswordFor(null)}
      />

      <ConfirmDialog
        open={!!confirmInvite}
        onClose={() => setConfirmInvite(null)}
        onConfirm={() => confirmInvite && invite.mutate(confirmInvite.id)}
        title={confirmInvite ? `Email ${confirmInvite.name} a password link?` : ''}
        confirmLabel="Send email"
        loading={invite.isPending}
      >
        A link to set their password goes to {confirmInvite?.email}. It works for 60 minutes. Their current
        password keeps working until they use it.
      </ConfirmDialog>

      <ConfirmDialog
        open={!!confirmSignIn}
        onClose={() => setConfirmSignIn(null)}
        onConfirm={() => confirmSignIn && signIn.mutate(confirmSignIn)}
        title={confirmSignIn ? `Sign in as ${confirmSignIn.name}?` : ''}
        confirmLabel="Sign in as them"
        loading={signIn.isPending}
      >
        You will see the {confirmSignIn ? consoleLabel(confirmSignIn) : 'console'} exactly as they do, for up to two
        hours. A banner stays on screen with a button to come back here. Anything you do there is recorded under
        their name, so use it to look, not to work.
      </ConfirmDialog>

      <ConfirmDialog
        open={!!confirmRemove}
        onClose={() => setConfirmRemove(null)}
        onConfirm={() => confirmRemove && remove.mutate(confirmRemove.id)}
        title="Deactivate this account?"
        confirmLabel="Deactivate"
        variant="danger"
        loading={remove.isPending}
      >
        {confirmRemove?.name} will be signed out immediately and will not be able to sign back in. Their production
        history stays on the jobs they worked, which is why the account is deactivated rather than deleted.
      </ConfirmDialog>
    </div>
  )
}
